import {
  MessageTemplate,
  MessageTemplateList,
  OkResponse,
  TEMPLATE_PLACEHOLDERS,
  renderTemplate,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Field } from '../../../components/Form';
import { Alert, ErrorBox, Skeleton } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { api, newIdempotencyKey } from '../../../lib/api';
import { errorMessage } from '../../../lib/messages';

const SAMPLE = {
  cliente: 'Mariana',
  produto: 'Vaso Ondas M',
  preco_unitario: 'R$ 54,90',
  quantidade: '2',
  total: 'R$ 109,80',
  prazo: '2 dias',
  validade: '13/10/2026',
  rastreio: 'AA123456789BR',
  numero_pedido: 'P-2026-0125',
};
const key = ['message-templates'] as const;

/** Modelos de mensagem tab: list + editor with placeholder chips and live preview. */
export function TemplatesTab({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const templates = useQuery({
    queryKey: key,
    queryFn: () => api.get('/message-templates', MessageTemplateList),
  });
  /** A template id, `'new'` while creating one, or null for the first template. */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; body: string } | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const [createKey, setCreateKey] = useState(newIdempotencyKey);

  const list = templates.data?.data ?? [];
  const current =
    selectedId === 'new' ? null : (list.find((t) => t.id === selectedId) ?? list[0] ?? null);
  const shown =
    draft ?? (current ? { name: current.name, body: current.body } : { name: '', body: '' });
  const dirty = draft !== null;

  const save = useMutation({
    mutationFn: () =>
      selectedId === 'new' || !current
        ? api.post('/message-templates', MessageTemplate, shown, { idempotencyKey: createKey })
        : api.patch(`/message-templates/${current.id}`, MessageTemplate, shown),
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: key });
      setSelectedId(t.id);
      setDraft(null);
      setCreateKey(newIdempotencyKey());
      toast('Modelo salvo');
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/message-templates/${id}`, OkResponse),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      setSelectedId(null);
      setDraft(null);
      toast('Modelo excluído. Fica na lixeira por 30 dias.');
    },
  });

  const insert = (token: string) => {
    const el = textRef.current;
    const body = shown.body;
    const at = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? at;
    setDraft({ ...shown, body: `${body.slice(0, at)}{${token}}${body.slice(end)}` });
    requestAnimationFrame(() => {
      el?.focus();
      const pos = at + token.length + 2;
      el?.setSelectionRange(pos, pos);
    });
  };

  if (templates.isPending) return <Skeleton rows={3} />;
  if (templates.isError)
    return (
      <ErrorBox
        title="Não foi possível carregar os modelos"
        onRetry={() => void templates.refetch()}
      />
    );

  return (
    <section className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="row between" style={{ padding: '16px 18px' }}>
        <h2>Modelos de mensagem</h2>
        {!readOnly ? (
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              setSelectedId('new');
              setDraft({ name: '', body: 'Oi, {cliente}! ' });
            }}
          >
            + Novo modelo
          </button>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', borderTop: '1px solid var(--line)' }}>
        <nav
          className="col"
          style={{ flex: '1 1 220px', gap: 2, padding: 10, borderRight: '1px solid var(--line)' }}
          aria-label="Modelos"
        >
          {list.map((t) => (
            <button
              key={t.id}
              type="button"
              className={current?.id === t.id && selectedId !== 'new' ? 'navi on' : 'navi'}
              style={{
                border: 0,
                background:
                  current?.id === t.id && selectedId !== 'new' ? undefined : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
              }}
              aria-current={current?.id === t.id ? 'true' : undefined}
              onClick={() => {
                setSelectedId(t.id);
                setDraft(null);
              }}
            >
              {t.name}
            </button>
          ))}
          {selectedId === 'new' ? <span className="navi on">Novo modelo</span> : null}
        </nav>
        <div className="col" style={{ flex: '3 1 420px', minWidth: 0, gap: 14, padding: 18 }}>
          <Field id="tn" label="Nome">
            <input
              id="tn"
              className="input"
              value={shown.name}
              onChange={(e) => setDraft({ ...shown, name: e.target.value })}
              disabled={readOnly}
            />
          </Field>
          <Field id="tx" label="Texto">
            <textarea
              id="tx"
              ref={textRef}
              className="input"
              style={{ height: 150, padding: 12, resize: 'vertical', whiteSpace: 'pre-wrap' }}
              value={shown.body}
              onChange={(e) => setDraft({ ...shown, body: e.target.value })}
              disabled={readOnly}
            />
          </Field>
          {!readOnly ? (
            <div className="row wrap" style={{ gap: 6 }}>
              <span className="xs muted" style={{ marginRight: 4 }}>
                Inserir:
              </span>
              {TEMPLATE_PLACEHOLDERS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className="chip"
                  style={{ minHeight: 32, fontSize: 13 }}
                  onClick={() => insert(p)}
                >
                  {`{${p}}`}
                </button>
              ))}
            </div>
          ) : null}
          <div className="card sunk col" style={{ gap: 6, padding: 14 }} aria-live="polite">
            <span className="xs muted">Prévia com dados de exemplo</span>
            <p style={{ whiteSpace: 'pre-line' }}>{renderTemplate(shown.body, SAMPLE)}</p>
          </div>
          {save.isError || remove.isError ? (
            <Alert tone="err">{errorMessage(save.error ?? remove.error)}</Alert>
          ) : null}
          {!readOnly ? (
            <div className="row wrap" style={{ gap: 10, justifyContent: 'flex-end' }}>
              <span className="xs muted grow">{dirty ? 'Alterações não salvas' : ''}</span>
              {current && selectedId !== 'new' ? (
                <button
                  type="button"
                  className="btn g"
                  onClick={() => remove.mutate(current.id)}
                  style={{ color: 'var(--err)' }}
                >
                  Excluir
                </button>
              ) : null}
              <button
                type="button"
                className="btn"
                disabled={!dirty}
                onClick={() => {
                  setDraft(null);
                  if (selectedId === 'new') setSelectedId(null);
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn p"
                disabled={!dirty || save.isPending}
                onClick={() => {
                  if (shown.name.trim() === '') {
                    toast('Dê um nome ao modelo');
                    return;
                  }
                  save.mutate();
                }}
              >
                Salvar modelo
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
